import { Form, InputGroup, Button, Row, Col, Table, Alert } from 'react-bootstrap';
import { connect, useSelector } from 'react-redux';
import axiosInstance from '../general/axiosInstance';
import React, { useEffect, useState } from 'react';
import { setIsLoading } from '../general/actions';
import { currencyFormater, downloadExcel } from '../general/utils';
import SelectAgreement from '../general/selectAgreement';
import { C, semaphoreColor, semaphoreEmoji } from '../general/colors';

function FundBar({ spent, amount }) {
    const features = useSelector(state => state.features);
    const pct = amount > 0 ? Math.min(100, (spent / amount) * 100) : 0;
    const color = semaphoreColor(pct, features);
    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--abi-muted)', marginBottom: 3 }}>
                <span>{currencyFormater(spent)}</span>
                <span style={{ color, fontWeight: 700 }}>{pct.toFixed(0)}%</span>
            </div>
            <div className="report-fund-bar-wrap">
                <div className="report-fund-bar-fill" style={{ width: `${pct}%`, background: color }} />
            </div>
        </div>
    );
}

function FundsReport(props) {
    const { setIsLoading } = props;

    const [funds, setFunds] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [agreementFilter, setAgreementFilter] = useState(0);
    const [amountTotal, setAmountTotal] = useState(0);
    const [spentTotal, setSpentTotal] = useState(0);
    const [availableTotal, setAvailableTotal] = useState(0);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('warning');

    useEffect(() => {
        if (agreementFilter) {
            setSearchText('');
            getFunds();
        } else {
            setFunds([]);
            setMessageType('info');
            setMessage('Selecciona un contrato para ver los fondos');
        }
    }, [agreementFilter]);

    useEffect(() => {
        setAmountTotal(funds.reduce((acc, f) => acc + parseFloat(f.amount), 0));
        setSpentTotal(funds.reduce((acc, f) => acc + parseFloat(f.spent), 0));
        setAvailableTotal(funds.reduce((acc, f) => acc + parseFloat(f.available), 0));
    }, [funds]);

    const getFunds = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/funds/agreements/' + agreementFilter);
            setFunds(response.data);
            if (response.data.length) {
                setMessage('');
            } else {
                setMessageType('info');
                setMessage('El contrato seleccionado no tiene fondos asociados');
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const filtered = funds.filter(item =>
        (item?.ext_id?.toLowerCase().includes(searchText.toLowerCase())) ||
        (item?.name?.toLowerCase().includes(searchText.toLowerCase()))
    );

    const totalPct = amountTotal > 0 ? Math.min(100, (spentTotal / amountTotal) * 100) : 0;
    const totalBarColor = semaphoreColor(totalPct, props.features);
    const semaphoreCfg = props.features && props.features.budget_semaphore;
    const semaphoreOn = !!(semaphoreCfg && semaphoreCfg.enabled);
    const semWarningPct = ((semaphoreCfg && semaphoreCfg.warning) ?? 0.60) * 100;
    const semDangerPct = ((semaphoreCfg && semaphoreCfg.danger) ?? 0.85) * 100;

    return (
        <>
            {/* ── Ayuda ── */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, background: 'rgba(37,99,235,0.05)', border: '1px solid rgba(37,99,235,0.15)', borderRadius: 12, padding: '12px 16px', marginBottom: 14 }}>
                <i className="fa fa-lightbulb-o" style={{ color: 'var(--abi-primary)', fontSize: 18, marginTop: 2, flexShrink: 0 }} />
                <div style={{ fontSize: 13, color: 'var(--abi-text)', lineHeight: 1.65 }}>
                    <strong>Reporte de Fondos:</strong> muestra la ejecución presupuestal de cada fondo dentro del contrato seleccionado.
                    La barra de progreso se colorea según el nivel de consumo —{' '}
                    {semaphoreOn ? (
                        <>
                            <span style={{ color: C.success, fontWeight: 600 }}>verde hasta {semWarningPct.toFixed(0)}%</span>,{' '}
                            <span style={{ color: C.info, fontWeight: 600 }}>azul {(semWarningPct + 1).toFixed(0)}–{semDangerPct.toFixed(0)}%</span>,{' '}
                            <span style={{ color: C.danger, fontWeight: 600 }}>rojo desde {(semDangerPct + 1).toFixed(0)}%</span>.
                        </>
                    ) : (
                        <>
                            <span style={{ color: C.success, fontWeight: 600 }}>verde &lt; 50%</span>,{' '}
                            <span style={{ color: C.warning, fontWeight: 600 }}>amarillo 50–80%</span>,{' '}
                            <span style={{ color: C.danger, fontWeight: 600 }}>rojo &gt; 80%</span>.
                        </>
                    )}
                    La fila de totales al pie resume el presupuesto global del contrato.
                </div>
            </div>

            {/* ── Panel de filtros ── */}
            <div className="order-section">
                <div className="order-section-header">
                    <i className="fa fa-money" /> Reporte de Fondos
                </div>
                <div className="order-section-body">
                    <Row className="g-3 align-items-end">
                        <Col md={7} xs={12}>
                            <Form.Label className="report-field-label">Contrato</Form.Label>
                            <SelectAgreement agreement={agreementFilter} setAgreement={setAgreementFilter} disabled={false} />
                        </Col>
                        <Col md={5} xs={12}>
                            <Form.Label className="report-field-label">Buscar fondo</Form.Label>
                            <InputGroup>
                                <InputGroup.Text style={{ background: 'var(--abi-bg)', border: '1px solid var(--abi-border)', borderRight: 'none' }}>
                                    <i className="fa fa-search" style={{ color: 'var(--abi-muted)', fontSize: 12 }} />
                                </InputGroup.Text>
                                <Form.Control
                                    placeholder="Nombre o código de fondo…"
                                    type="text"
                                    value={searchText}
                                    disabled={!funds.length}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    style={{ borderLeft: 'none' }}
                                />
                                {searchText && (
                                    <Button variant="outline-secondary" onClick={() => setSearchText('')}>
                                        <i className="fa fa-times" />
                                    </Button>
                                )}
                            </InputGroup>
                        </Col>
                    </Row>
                </div>
            </div>

            {message && <Alert variant={messageType || 'warning'} className="mt-2">{message}</Alert>}

            {funds.length > 0 && (
                <>
                    <div className="report-toolbar">
                        <span className="report-count">
                            <strong>{filtered.length}</strong> de <strong>{funds.length}</strong> fondo{funds.length !== 1 ? 's' : ''}
                        </span>
                        <div className="d-flex gap-2">
                            <Button variant="outline-success" size="sm" onClick={() => {
                                const headers = ['Centro de Costos', 'ID Fondo', 'Ext ID', 'Nombre', 'Valor inicial', 'Consumo', 'Disponible', '% Consumido', 'Semáforo', 'Estado'];
                                const rows = filtered.map(f => {
                                    const pct = f.amount > 0 ? Math.min(100, (f.spent / f.amount) * 100) : 0;
                                    return [
                                        `${f.department?.id}: ${f.department?.name}`,
                                        f.id,
                                        f.ext_id,
                                        f.name,
                                        parseFloat(f.amount),
                                        parseFloat(f.spent),
                                        parseFloat(f.available),
                                        parseFloat(pct.toFixed(2)),
                                        semaphoreEmoji(pct, props.features),
                                        f.is_active ? 'Activo' : 'Inactivo',
                                    ];
                                });
                                const colFormats = [null, null, null, null, 'currency', 'currency', 'currency', 'percent', null, null];
                                downloadExcel(headers, rows, 'Reporte de fondos', 'Fondos', colFormats);
                            }}>
                                <i className="fa fa-file-excel-o" /> Excel
                            </Button>
                            <Button variant="outline-primary" size="sm" title="Actualizar" onClick={getFunds}>
                                <i className="fa fa-rotate-right" />
                            </Button>
                        </div>
                    </div>

                    <Table responsive className="report-table" id="funds">
                        <thead>
                            <tr>
                                <th>Centro de Costos</th>
                                <th>Fondo</th>
                                <th>Nombre</th>
                                <th>Valor Inicial</th>
                                <th>Consumo</th>
                                <th>Disponible</th>
                                <th>Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(item => (
                                <tr key={'fund-' + item.id}>
                                    <td style={{ fontSize: 13 }}>
                                        <span style={{ fontWeight: 600 }}>{item.department.id}</span>
                                        <span style={{ color: 'var(--abi-muted)' }}>: {item.department.name}</span>
                                    </td>
                                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{item.id}:{item.ext_id}</td>
                                    <td style={{ fontWeight: 600, fontSize: 13 }}>{item.name}</td>
                                    <td>
                                        <div style={{ fontWeight: 700 }}>{currencyFormater(item.amount)}</div>
                                        <FundBar spent={item.spent} amount={item.amount} />
                                    </td>
                                    <td style={{ fontWeight: 600 }}>{currencyFormater(item.spent)}</td>
                                    <td style={{ fontWeight: 700, color: parseFloat(item.available) <= 0 ? C.danger : C.success }}>
                                        {currencyFormater(item.available)}
                                    </td>
                                    <td>
                                        <span className="report-status-badge" style={
                                            item.is_active
                                                ? { background: '#dcfce7', color: '#166534', border: '1px solid #86efac' }
                                                : { background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca' }
                                        }>
                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor', display: 'inline-block', marginRight: 4 }} />
                                            {item.is_active ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        {!searchText && (
                            <tfoot>
                                <tr className="report-tfoot">
                                    <th colSpan={3}>Total general</th>
                                    <th>
                                        <div style={{ fontWeight: 800 }}>{currencyFormater(amountTotal)}</div>
                                        <div className="report-fund-bar-wrap" style={{ marginTop: 5 }}>
                                            <div className="report-fund-bar-fill" style={{ width: `${totalPct}%`, background: totalBarColor }} />
                                        </div>
                                        <div style={{ fontSize: 10, marginTop: 3, opacity: 0.85 }}>{totalPct.toFixed(0)}% consumido</div>
                                    </th>
                                    <th>{currencyFormater(spentTotal)}</th>
                                    <th>{currencyFormater(availableTotal)}</th>
                                    <th></th>
                                </tr>
                            </tfoot>
                        )}
                    </Table>
                </>
            )}
        </>
    );
}

function mapStateToProps(state) {
    return { isLoading: state.isLoading, features: state.features };
}

const mapDispatchToProps = { setIsLoading };

export default connect(mapStateToProps, mapDispatchToProps)(FundsReport);
